import React, { useEffect, useState } from "react";
import {useTheme, useMediaQuery} from "@mui/material";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import styled from "@emotion/styled/macro";
import {
    Button, 
    Grid2 as Grid,
    Popper,
    Fade,
    Tooltip,
    Typography,
    Slide
} from "@mui/material";
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineOppositeContent,
    timelineContentClasses
} from '@mui/lab';


import StudyIcon from '@mui/icons-material/AutoStoriesOutlined';
import CareerIcon from '@mui/icons-material/WorkOutlineOutlined';
import CertificationsIcon from '@mui/icons-material/CardMembershipOutlined';
import ProjectsIcon from '@mui/icons-material/IntegrationInstructionsOutlined';
import EpiphanyIcon from '@mui/icons-material/EmojiObjectsOutlined';
import LegendToggleIcon from '@mui/icons-material/LegendToggleOutlined';

import TIMELINE from "./content";

const StyledLegendPopper = styled(Popper)`
    width: 170px;
    background-color: white;
    border-radius: 3px;
    box-shadow: 3px 4px 3px rgba(0, 0, 0, 0.25);
`;

const StyledLegendExpandButton = styled(Button)`
    position: fixed;
    top: 12%;
    left: 1%;
    color: black;
    font-family: poppins;
    background-color: white;
    z-index: 2;
    border-radius: 10px;
    border: solid 2px;
    transition: all 0.50s;

    &:hover {
        scale: 1.10;
    }
    &:active {
        scale: 1.05;
    }
`;
const responsiveLegendExpandButtonStyle = {
    top: {
        xs: "255px",
        sm: "30%",
        md: "92px",
        lg: "92px"
    },
    left: {
        xs: "1%",
        sm: "1%",
        md: "1%",
        lg: "1%"
    },
    "&:active": {
        top: {
            xs: "256px",
            sm: "30.1%",
            md: "93px",
            lg: "93px"
        }
    }
}
const responsiveLegendExpandButtonScrolledStyle = {
    top: {
        xs: "92px",
        sm: "92px",
        md: "92px",
        lg: "92px"
    },
    left: {
        xs: "1%",
        sm: "1%",
        md: "1%",
        lg: "1%"
    },
    "&:active": {
        top: {
            xs: "93px",
            sm: "93px",
            md: "93px",
            lg: "93px"
        }
    }
}
const StyledLegendGridContainer = styled(Grid)`
    font-family: poppins;
    margin-top: 1rem;
    pointer-events: none;
    align-items: center;
    text-align: center;
    width: 100px;
`;

const StyledLegendHeading = styled(Typography)`
    font-family: poppins;
    font-weight: bold;
`;

const StyledLegendSubtitle = styled(Typography)`
    font-family: poppins;
    padding-left: 3px;
`;

const StyledLegendCareerIcon = styled(CareerIcon)`
    height: 25px;
    width: 25px;
    color: white;
    background-color: #1976d2;
    border-radius: 50%;
    padding: 10%;
`;

const StyledLegendCertificationIcon = styled(CertificationsIcon)`
    height: 25px;
    width: 25px;
    color: white;
    background-color: #d32f2f;
    border-radius: 50%;
    padding: 10%;
`;
const StyledLegendStudyIcon = styled(StudyIcon)`
    height: 25px;
    width: 25px;
    color: white;
    background-color: #ed6c02;
    border-radius: 50%;
    padding: 10%;
`;
const StyledLegendProjectsIcon = styled(ProjectsIcon)`
    height: 25px;
    width: 25px;
    color: white;
    background-color: #2e7d32;
    border-radius: 50%;
    padding: 10%;
`;
const StyledLegendEpiphanyIcon = styled(EpiphanyIcon)`
    height: 25px;
    width: 25px;
    color: white;
    background-color: #9c27b0;
    border-radius: 50%;
    padding: 10%;
`;

const StyledHistoricalTimelineHeader = styled(Typography)`
    font-family: poppins;
    text-align: center;
    margin: 5vh;
    pointer-events: none;
`;

const StyledTimelineEventHeading = styled(Typography)`
    font-family: poppins;
`;

const StyledTimelineDescription = styled(Typography)`
    font-family: poppins;
    color: gray;
`;
const responsiveTimeline = {
    pointerEvents: "none",
    marginBottom: "10vh",
    [`& .${timelineContentClasses}`]: {
        flex: {
            xs: 0.2,
            msm: "unset"
        }
    }
};
const responsiveTimelineOppositeContent = {
    paddingTop: "20px"
};
const responsiveTimelineContent = {
    paddingBottom: {
        xs: "40px"
    }
};
const responsiveTimelineHeading = {
    fontSize: {
        xs: "medium",
        msm: "large",
        md: "larger"
    }
};
const responsiveTimelineDescription = {
    fontSize: {
        xs: "small",
        msm: "medium",
        md: "medium"
    }
};

const HistoricalTimeline = ({scrollPosition}) => {
    let history = useHistory();
    const theme = useTheme();
    const [legendOpen, setLegendOpen] = useState(false);
    const [legendPopperAnchorEl, setLegendPopperAnchorEl] = useState(null);
    const breakpointMsm = useMediaQuery(theme.breakpoints.down("msm"));

    useEffect(() => {
        if(history.action !== "POP"){
            window.scrollTo({
                top: 0
            });
        }
    }, [history]);

    const handleLegendExpansionClick = (event) => {
        setLegendPopperAnchorEl(legendPopperAnchorEl ? null: event.currentTarget);
        toggleLegend();
    }

    const toggleLegend = () => {
        setLegendOpen(!legendOpen);
    }

    const determineLandmarkSlideDirection = (idx, landmarksArr) => {
        let idxEven = idx % 2 === 0;
        if(landmarksArr.length % 2 === 0){
            if(!idxEven){
                return "left";
            }
            return "right";
        }else{
            if(!idxEven){
                return "right";
            }
            return "left";
        }
    }

    const determineLandmarkIcon = (landmarkType) => {
        let { LANDMARK_TYPES } = TIMELINE;
        switch(landmarkType){
            case LANDMARK_TYPES.STUDY: {
                return (
                    <TimelineDot variant="filled" color="warning">
                        <StudyIcon />
                    </TimelineDot>
                )
            }
            case LANDMARK_TYPES.CAREER: {
                return (
                    <TimelineDot variant="filled" color="primary">
                        <CareerIcon />
                    </TimelineDot>
                )
            }
            case LANDMARK_TYPES.CERTIFICATION: {
                return (
                    <TimelineDot variant="filled" color="error">
                        <CertificationsIcon />
                    </TimelineDot>
                )
            }
            case LANDMARK_TYPES.PROJECTS: {
                return (
                    <TimelineDot variant="filled" color="success">
                        <ProjectsIcon />
                    </TimelineDot>
                )
            }
            case LANDMARK_TYPES.EPIPHANY: {
                return (
                    <TimelineDot variant="filled" color="secondary">
                        <EpiphanyIcon />
                    </TimelineDot>
                )
            }
            default: {
                return (
                    <TimelineDot />
                )
            }
        }
    }

    return ( 
        <React.Fragment>
            <Slide direction="up" in mountOnEnter unmountOnExit>
                <Tooltip title={legendOpen? "Close Legend" : "Open Legend"} placement="right">
                    <StyledLegendExpandButton onClick={handleLegendExpansionClick} sx={scrollPosition <= 100 ? responsiveLegendExpandButtonStyle : responsiveLegendExpandButtonScrolledStyle}>
                        <LegendToggleIcon />
                    </StyledLegendExpandButton>
                </Tooltip>
            </Slide>
            <StyledLegendPopper open={legendOpen} anchorEl={legendPopperAnchorEl} placement="bottom-start" transition>
                 {({ TransitionProps }) =>(
                     <Fade {...TransitionProps} mountOnEnter unmountOnExit timeout={150}>
                        <StyledLegendGridContainer container rowSpacing={1}>
                            <Grid item size={{xs: 12}}>
                                <StyledLegendHeading>Event Types</StyledLegendHeading>
                            </Grid>
                            <Grid item size={{xs: 6}}>
                                <StyledLegendCareerIcon />
                            </Grid>
                            <Grid item size={{xs: 6}}>
                                <StyledLegendSubtitle>
                                    Career    
                                </StyledLegendSubtitle>
                            </Grid>
                            <Grid item size={{xs: 6}}>
                                <StyledLegendStudyIcon />
                            </Grid>
                            <Grid item size={{xs: 6}}>
                                <StyledLegendSubtitle>
                                    Studies    
                                </StyledLegendSubtitle>
                            </Grid>
                            <Grid item size={{xs: 6}}>
                                <StyledLegendCertificationIcon />
                            </Grid>
                            <Grid item size={{xs: 6}}>
                                <StyledLegendSubtitle>
                                    Certifications 
                                </StyledLegendSubtitle>
                            </Grid>
                            <Grid item size={{xs: 6}}>
                                <StyledLegendProjectsIcon />
                            </Grid>
                            <Grid item size={{xs: 6}}>
                                <StyledLegendSubtitle>
                                    Projects    
                                </StyledLegendSubtitle>
                            </Grid>
                            <Grid item size={{xs: 6}}>
                                <StyledLegendEpiphanyIcon />
                            </Grid>
                            <Grid item size={{xs: 6}}>
                                <StyledLegendSubtitle>
                                    Epiphanies    
                                </StyledLegendSubtitle>
                            </Grid>
                        </StyledLegendGridContainer>
                     </Fade>
                 )}
            </StyledLegendPopper>
            <Slide direction="down" in mountOnEnter unmountOnExit>
                <StyledHistoricalTimelineHeader variant="h3">
                    Keane&apos;s Timeline
                </StyledHistoricalTimelineHeader>
            </Slide>
                <Timeline position={breakpointMsm? "" : "alternate"} sx={responsiveTimeline}>
                    {TIMELINE.LANDMARKS.map((landmark, idx) => (
                        <Slide key={landmark} direction={determineLandmarkSlideDirection(idx, TIMELINE.LANDMARKS)} in mountOnEnter unmountOnExit timeout={400}>
                            <TimelineItem key={landmark}>
                                <TimelineOppositeContent variant="body2" sx={responsiveTimelineOppositeContent}>
                                    {landmark.date}
                                </TimelineOppositeContent>
                                <TimelineSeparator>
                                    {
                                        determineLandmarkIcon(landmark.type)
                                    }
                                    <TimelineConnector />
                                </TimelineSeparator>
                                <TimelineContent sx={responsiveTimelineContent}>
                                    <StyledTimelineEventHeading variant="h6" sx={responsiveTimelineHeading}>{landmark.event}</StyledTimelineEventHeading>
                                    <StyledTimelineDescription sx={responsiveTimelineDescription}>{landmark.description}</StyledTimelineDescription>
                                </TimelineContent>
                            </TimelineItem>
                        </Slide>
                        )).reverse()
                    }
                    <Slide direction="up" in mountOnEnter unmountOnExit timeout={400}>
                        <TimelineItem>
                            <TimelineOppositeContent variant="body2" sx={responsiveTimelineContent}>
                                1991
                            </TimelineOppositeContent>
                            <TimelineSeparator>
                                <TimelineDot variant="filled" color="secondary">
                                    <EpiphanyIcon />
                                </TimelineDot>
                                </TimelineSeparator>
                            <TimelineContent>
                                <StyledTimelineEventHeading variant="h6" sx={responsiveTimelineHeading}>Birth</StyledTimelineEventHeading>
                                <StyledTimelineDescription sx={responsiveTimelineDescription}>Hello world!</StyledTimelineDescription>
                            </TimelineContent>
                        </TimelineItem>
                    </Slide>
                </Timeline>
                
        </React.Fragment>
    );
}
 


export default HistoricalTimeline;